import { ChangeDetectionStrategy, Component, computed, inject, input, signal } from '@angular/core';
import { MarkdownComponent } from 'ngx-markdown';
import { TranslocoModule } from '@ngneat/transloco';
import { SharedModule } from '@shared/shared.module';
import { DOCK_RENDER_ZONE } from '@shared/floating-panel/dock-render-zone.token';
import { HdCircuitInfoService } from '../hd-circuit-info.service';
import { HD_BODYGRAPH_CIRCUIT_THEME_ICONS, getHdBodygraphCircuit } from '../hd-bodygraph-circuit-metadata';
import { HdCircuitInfoContext } from './hd-circuit-info-context.model';
import {
  HD_CIRCUIT_INFO_DEFAULT_VARIANT,
  HD_CIRCUIT_INFO_VARIANTS,
  HdCircuitInfoVariant,
} from './hd-circuit-info-variant';
import { HdCircuitInfoKernwoordenComponent } from './shared/hd-circuit-info-kernwoorden.component';
import {
  ESegmentedToggleComponent,
  ESegmentedToggleOption,
} from '@shared/components/atoms/inputs/e-segmented-toggle/e-segmented-toggle.component';

/**
 * Volledige info-weergave voor één circuit. Toont een header (theme-icoon +
 * circuit-naam + Engelse titel/type), een kernwoorden-rij (chips) en de
 * markdown-body voor de gekozen variant (`compact` / `normal` / `extended`)
 * via ngx-markdown.
 *
 * Werkt als **content-component**: krijgt de context via input zodat hij
 * herbruikbaar is buiten een floating-panel context. Wanneer hij ingespoten
 * wordt door {@link HdCircuitInfoPanelComponent} (de floating-panel wrapper),
 * leest die wrapper het `FLOATING_PANEL_DATA`-signaal uit en zet het als
 * input op deze component.
 *
 * Spiegelt het structurele patroon van {@link HdGateInfoFullComponent} en
 * herbruikt dezelfde stijl-markers (BW Modelica voor titels, amber-accenten
 * op secties) — zie `.scss`.
 */
@Component({
  selector: 'hd-circuit-info-full',
  templateUrl: './hd-circuit-info-full.component.html',
  styleUrl: './hd-circuit-info-full.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    MarkdownComponent,
    SharedModule,
    TranslocoModule,
    HdCircuitInfoKernwoordenComponent,
    ESegmentedToggleComponent,
  ],
})
export class HdCircuitInfoFullComponent {
  private readonly circuitInfoService = inject(HdCircuitInfoService);

  /**
   * `null` wanneer de component ingespoten is in een gedockte tab. `inject`
   * is optioneel zodat dezelfde component evengoed buiten een floating panel
   * gebruikt kan worden (bv. op een full-page weergave).
   */
  private readonly dockRenderZone = inject(DOCK_RENDER_ZONE, { optional: true });
  protected readonly isFloating = this.dockRenderZone == null;

  readonly context = input.required<HdCircuitInfoContext>();

  /** Door de gebruiker gekozen variant. Init op de uit de context geleverde of default. */
  protected readonly variant = signal<HdCircuitInfoVariant>(HD_CIRCUIT_INFO_DEFAULT_VARIANT);

  /** Door de parent te triggeren reset wanneer de context-key wijzigt. */
  constructor() {
    // De context-input is required en stabiel per panel-lifecycle; we
    // synchroniseren de variant eenmalig bij wijziging van de context.
    // Bewust geen `effect` op `context()`: een gebruiker mag de variant
    // los kunnen tunen zonder dat een re-bind van dezelfde context die
    // keuze ongedaan maakt.
  }

  protected readonly circuitKey = computed(() => this.context().circuitKey);

  /** Vertaalde info voor dit circuit (volgt de actieve app-taal). */
  protected readonly circuit = computed(() =>
    this.circuitInfoService.circuitTranslation(this.circuitKey()),
  );

  /** Theme-icoon (FA naam zonder prefix) voor dit circuit, of `null` als onbekend. */
  protected readonly themeIcon = computed<string | null>(() => {
    const def = getHdBodygraphCircuit(this.circuitKey());
    if (!def) return null;
    return HD_BODYGRAPH_CIRCUIT_THEME_ICONS[def.theme] ?? null;
  });

  /** Markdown-asset-URL voor de geselecteerde variant. */
  protected readonly markdownSrc = computed(() =>
    this.circuitInfoService.markdownUrl(this.circuitKey(), this.variant()),
  );

  /** Volledige UI-labels (variant-switcher, kernwoorden-titel, …). */
  protected readonly ui = computed(() => this.circuitInfoService.uiLabels());

  /** Knoppen van de variant-toggle (compact/normaal/uitgebreid, gedeelde e-segmented-toggle). */
  protected readonly variantOptions = computed<readonly ESegmentedToggleOption<HdCircuitInfoVariant>[]>(() => {
    const u = this.ui();
    const labels: Readonly<Record<HdCircuitInfoVariant, string>> = {
      compact: u.variantCompact,
      normal: u.variantNormal,
      extended: u.variantExtended,
    };
    return HD_CIRCUIT_INFO_VARIANTS.map(variant => ({ value: variant, label: labels[variant] }));
  });

  protected setVariant(variant: HdCircuitInfoVariant): void {
    this.variant.set(variant);
  }
}
